import { useState, useRef } from "react";
import { Tab, Tabs, TabList, TabPanel } from "react-tabs";
import WallControl from "./walls/WallControl";
import YurtControl from "./walls/yurt/YurtControl";
import "react-tabs/style/react-tabs.css";
import { useFirestore } from "react-redux-firebase";
import { createStack } from "./walls/yurt/entities/stacks/createStack";
import { useReactToPrint } from "react-to-print";
import AllDrawings from "./AllDrawings";
import SheetControl from "./sheets/SheetControl";
import OrderControl from "./orders/OrderControl";
import styled from "styled-components";

function isNumeric(str) {
  if (typeof str != "string") return false;
  return !isNaN(str) && !isNaN(parseFloat(str));
}

const Select = styled.div`
  outline: none !important;
`;

const Buttons = styled.div`
  display: flex;
  justify-content: flex-end;
  /* background: #4b524d; */
  border-radius: 3px;
  margin: 5px 0;
`;

const Button = styled.div`
  color: ${({ selected }) => (selected ? "var(--primary)" : "white")};
  padding: 5px 20px;

  text-transform: uppercase;
  font-weight: 700;
  background: ${({ selected }) => (selected ? "white" : "var(--primary)")};
  border: 1px solid var(--primary);
  border-radius: 3px;
  cursor: pointer;
  transform: ${({ selected }) => (selected ? "scale(1.1)" : "")};
`;

const Delete = styled.div`
  padding: 20px;
  color: red;
  background: transparent;
  border: 1px solid red;
  border-radius: 3px;
  margin: 20px 0;
  width: max-content;
  cursor: pointer;
  &:hover {
    background: red;
    color: white;
  }
`;

const BuildControl = ({ project, handleDelete }) => {
  const firestore = useFirestore();

  const [pixels] = useState(6);
  const [yurtTrait, setYurtTrait] = useState({
    kitName: project.kitName,
    size: project.size,
    h: project.h,
    bottomPlate: project.bottomPlate,
    eaves: project.eaves,
    stud: project.stud,
    stack: project.stack,
    placement: project.placement,
    nailingPattern: project.nailingPattern,
    allNotes: project.allNotes,
    id: project.id,
  });
  const [walls, setWalls] = useState(project.walls);
  const stackHeights = [];

  const handleYurtTraitChange = (e) => {
    let value = e.target.value;
    let name = e.target.name;

    if (name === "h" || name === "stud") {
      value = value * pixels;
    }

    if (name === "stack") {
      let stackArray = value.split(",");
      value = createStack(yurtTrait.size, walls, stackArray, stackHeights);
    }

    setYurtTrait({ ...yurtTrait, [name]: value });

    if (name === "size") {
      let newWalls = [];
      for (let i = 0; i < value; i++) {
        let newWall = {
          pixels: pixels,
          twoByW: 1.5 * pixels,
          twoByH: 3.5 * pixels,
          w: 48 * pixels,
          h: yurtTrait.h * pixels,
          stud: 16 * pixels,
          windows: 0,
          window1: {
            windowW: 42 * pixels,
            windowH: 42 * pixels,
            sill: 24 * pixels,
            header: 5.5 * pixels,
            headerAmount: 0,
            windowType: "FIXED",
            placement: 0,
            tempered: "STANDARD",
          },
          window2: {
            windowW: 42 * pixels,
            windowH: 42 * pixels,
            sill: 24 * pixels,
            header: 5.5 * pixels,
            headerAmount: 0,
            windowType: "FIXED",
            placement: 0,
            tempered: "STANDARD",
          },
          windowW: 42 * pixels,
          windowH: 42 * pixels,
          sill: 24 * pixels,
          header: 5.5 * pixels,
          headerAmount: 0,
          windowType: 0,
          placement: 0,
          id: i,
          notes: "",
          windowEdgeTrim: false,
          cornerTrim: false,
          kitName: yurtTrait.kitName,
          doors: 0,
          doorH: 82 * pixels,
          doorW: 38 * pixels,
          frenchDoor: null,
          frenchHeader: 9.25 * pixels,
        };
        newWalls.push(newWall);
      }
      setWalls(newWalls);

      return firestore.update(
        { collection: "projects", doc: project.id },
        { ...project, [name]: value, walls: newWalls }
      );
    } else {
      return firestore.update(
        { collection: "projects", doc: project.id },
        { ...project, [name]: value }
      );
    }
  };

  const handleStackChange = (stackArray) => {
    let newStack = createStack(yurtTrait.size, walls, stackArray, stackHeights);
    setYurtTrait({ ...yurtTrait, stack: newStack });
    return firestore.update(
      { collection: "projects", doc: project.id },
      { ...project, stack: newStack }
    );
  };

  const handleWallChange = (e) => {
    let newWallArr = [...walls];
    if (e.target.parentElement.id === "form-windows") {
      if (isNumeric(e.target.value)) {
        let numericValue = e.target.value * pixels;

        if (
          newWallArr[
            (((e.target.form.id - 1) % newWallArr.length) + newWallArr.length) %
              newWallArr.length
          ][e.target.id][e.target.name] /
            pixels ===
          newWallArr[
            (((e.target.form.id + 1) % newWallArr.length) + newWallArr.length) %
              newWallArr.length
          ][e.target.id][e.target.name] /
            pixels
        ) {
        }
        newWallArr[e.target.form.id][e.target.id][e.target.name] = numericValue;
      } else {
        newWallArr[e.target.form.id][e.target.id][e.target.name] =
          e.target.value;
      }
    } else if (isNumeric(e.target.value)) {
      newWallArr[e.target.form.id][e.target.name] = e.target.value * pixels;
    } else {
      newWallArr[e.target.form.id][e.target.name] = e.target.value;
    }

    setWalls(newWallArr);

    let updateProject = project;
    updateProject.walls = newWallArr;

    return firestore.update(
      { collection: "projects", doc: project.id },
      updateProject
    );
  };

  const handleMasterWallChange = (id, name, value) => {
    let newWallArr = [...walls];

    if (id === "window1" || id === "window2") {
      newWallArr.forEach(
        (e) =>
          (e[id][name] = typeof value === "number" ? value * pixels : value)
      );
    } else {
      newWallArr.forEach(
        (e) => (e[name] = typeof value === "number" ? value * pixels : value)
      );
    }

    let updateProject = project;
    updateProject.walls = newWallArr;

    return firestore.update(
      { collection: "projects", doc: project.id },
      updateProject
    );
  };

  const [tabIndex, setTabIndex] = useState(0);
  const [printing, setPrinting] = useState(false);

  const handleTab = (e) => {
    if (e.code === "Tab") {
      e.preventDefault();
      setTabIndex(tabIndex <= yurtTrait.size - 1 ? tabIndex + 1 : 0);
    }
  };

  const componentRef = useRef();
  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    onBeforeGetContent: () => setPrinting(true),
  });

  let printState = null;
  if (printing) {
    printState = <AllDrawings walls={walls} yurtTrait={yurtTrait} />;
  }

  const printAll = () => {
    setPrinting(true);
    setTimeout(function () {
      handlePrint();
    }, 100);
    setTimeout(function () {
      setPrinting(false);
    }, 200);
  };

  const [view, setView] = useState("drawings");

  return (
    <>
      <Buttons>
        <Button
          selected={view === "drawings"}
          onClick={() => setView("drawings")}
        >
          drawings
        </Button>
        <Button selected={view === "orders"} onClick={() => setView("orders")}>
          orders
        </Button>
      </Buttons>
      {view === "drawings" ? (
        <Select tabIndex="0" onKeyDown={handleTab}>
          <Tabs
            selectedIndex={tabIndex}
            onSelect={(index) => setTabIndex(index)}
          >
            <TabList>
              <Tab>Yurt</Tab>
              {walls.map(function (wall, i) {
                return <Tab key={i}>{i + 1}</Tab>;
              })}
            </TabList>
            <TabPanel>
              <YurtControl
                yurtTrait={yurtTrait}
                wallTrait={walls}
                handleYurtTraitChange={handleYurtTraitChange}
                handleWallChange={handleWallChange}
                pixels={pixels}
                handleStackChange={handleStackChange}
                setTabIndex={setTabIndex}
                printAll={printAll}
                handleMasterWallChange={handleMasterWallChange}
                stackHeights={stackHeights}
              />
            </TabPanel>
            {walls.map(function (wall, i) {
              return (
                <TabPanel key={i}>
                  <WallControl
                    yurtTrait={project}
                    trait={wall}
                    wallNumber={i}
                    handleTraitChange={handleWallChange}
                    walls={walls}
                    setWalls={setWalls}
                  />
                </TabPanel>
              );
            })}
          </Tabs>
          <div ref={componentRef}>{printState}</div>
        </Select>
      ) : view === "orders" ? (
        <OrderControl yurtTrait={yurtTrait} walls={walls} id={project.id} />
      ) : (
        <></>
      )}

      <div>
        <SheetControl project={project} editProject={handleYurtTraitChange} />
      </div>
      <Delete onClick={() => handleDelete(project.id)}>Delete Project</Delete>
    </>
  );
};

export default BuildControl;
